import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../backend/firebase";
import {
  addDoc,
  collection,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  doc,
  limit,
  startAfter,
} from "firebase/firestore";
import { FaStar } from "react-icons/fa";

const RenderComment = React.memo(({ comment }) => {
  const [userDetails, setUserDetails] = useState(null);

  useEffect(() => {
    let unsubscribe;
    try {
      const userRef = doc(db, "Users", comment.commentedBy);
      unsubscribe = onSnapshot(userRef, (data) => {
        if (data.exists()) {
          setUserDetails(data.data());
        }
      });
    } catch (error) {
      console.warn("Error fetching user details:", error);
    }
    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [comment.commentedBy]);

  const validRating = Math.max(0, Math.min(5, Number(comment.rating) || 0));
  const commentDate = comment.commentedAt?.toDate
    ? new Date(comment.commentedAt.toDate()).toLocaleDateString()
    : "Just now";

  return (
    <div className="comment-card">
      <div className="comment-content">
        <p className="comment-text">{comment.comment}</p>
        <div className="comment-footer">
          <div className="star-rating">
            {[...Array(5)].map((_, i) => (
              <FaStar
                key={i}
                size={12}
                color={i < validRating ? "#667eea" : "#444"}
              />
            ))}
          </div>
          <span className="comment-meta">
            {userDetails?.displayName || comment.commentedBy} • {commentDate}
          </span>
        </div>
      </div>
    </div>
  );
});

const DatasetInteraction = ({ datasetDetails }) => {
  const [input, setInput] = useState("");
  const [comments, setComments] = useState([]);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const commentsEndRef = useRef(null);
  const unsubscribeRef = useRef(null);

  const COMMENTS_PER_PAGE = 5;

  const addComment = async () => {
    if (!input.trim() || rating === 0) return;

    try {
      const commentRef = collection(
        db,
        "datasets",
        datasetDetails.uuid,
        "Comments"
      );
      await addDoc(commentRef, {
        comment: input.trim(),
        rating,
        commentedAt: serverTimestamp(),
        commentedBy: auth.currentUser.uid,
      });
      setInput("");
      setRating(0);

      setPage(1);
      setHasMore(true);
      setLastVisible(null);
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const loadComments = () => {
    if (loading) return;

    setLoading(true);

    if (
      unsubscribeRef.current &&
      typeof unsubscribeRef.current === "function"
    ) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    try {
      const commentRef = collection(
        db,
        "datasets",
        datasetDetails.uuid,
        "Comments"
      );

      let q;
      if (page === 1 || !lastVisible) {
        q = query(
          commentRef,
          orderBy("commentedAt", "desc"),
          limit(COMMENTS_PER_PAGE)
        );
      } else {
        q = query(
          commentRef,
          orderBy("commentedAt", "desc"),
          startAfter(lastVisible),
          limit(COMMENTS_PER_PAGE)
        );
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const fetched = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          setComments(fetched);

          const newLastVisible = snapshot.docs[snapshot.docs.length - 1];
          setLastVisible(newLastVisible);

          setHasMore(fetched.length === COMMENTS_PER_PAGE);
          setLoading(false);
        },
        (error) => {
          console.error("Error loading comments:", error);
          setLoading(false);
        }
      );

      unsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error("Error setting up query:", error);
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (hasMore && !loading) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1 && !loading) {
      setPage((prev) => prev - 1);
    }
  };

  useEffect(() => {
    loadComments();

    return () => {
      if (
        unsubscribeRef.current &&
        typeof unsubscribeRef.current === "function"
      ) {
        unsubscribeRef.current();
      }
    };
  }, [page]);

  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [comments]);

  return (
    <div className="interaction-container">
      <div className="rating-section">
        <h3 className="section-title">Rate this dataset</h3>
        <div className="star-input-group">
          <div className="star-rating-input">
            {[1, 2, 3, 4, 5].map((star) => (
              <FaStar
                key={star}
                size={20}
                className="star-icon"
                color={star <= (hoverRating || rating) ? "#667eea" : "#555"}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
              />
            ))}
          </div>
          <span className="rating-text">
            {rating > 0
              ? `${rating} star${rating > 1 ? "s" : ""}`
              : "Select rating"}
          </span>
        </div>
      </div>

      <div className="comment-input-section">
        <textarea
          value={input}
          placeholder="Share your thoughts about this dataset..."
          onChange={(e) => setInput(e.target.value)}
          className="comment-input"
          rows={3}
        />
        <div className="input-controls">
          <span
            onClick={addComment}
            disabled={!input.trim() || rating === 0}
            className="submit-button"
          >
            Post Review
          </span>
        </div>
      </div>

      <div className="comments-section">
        <div className="comments-header">
          <h3 className="section-title">Community Reviews</h3>
          <div className="pagination-controls">
            <button
              onClick={handlePrevPage}
              disabled={page === 1 || loading}
              className="pagination-button"
            >
              ← Previous
            </button>
            <span className="page-indicator">Page {page}</span>
            <button
              onClick={handleNextPage}
              disabled={!hasMore || loading}
              className="pagination-button"
            >
              Next →
            </button>
          </div>
        </div>

        <div className="comments-list">
          {loading && comments.length === 0 ? (
            <div className="loading-spinner"></div>
          ) : comments.length > 0 ? (
            <>
              {comments.map((comment) => (
                <RenderComment key={comment.id} comment={comment} />
              ))}
              <div ref={commentsEndRef} />
            </>
          ) : (
            <p className="no-comments">
              No reviews yet. Be the first to share your thoughts!
            </p>
          )}
        </div>
      </div>

      <style jsx>{`
        .interaction-container {
          display: flex;
          flex-direction: column;
          gap: 2rem;
          padding: 2rem;
          background: #000000;
          border-radius: 12px;
          border: 1px solid #333;
        }

        .section-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #ffffff;
        }

        .rating-section {
          padding-bottom: 1.5rem;
        }

        .star-input-group {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .star-rating-input {
          display: flex;
          gap: 0.5rem;
        }

        .star-icon {
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .star-icon:hover {
          transform: scale(1.15);
        }

        .rating-text {
          font-size: 0.9rem;
          color: #aaa;
          font-weight: 500;
        }

        .comment-input-section {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          width: 95%
        }

        .comment-input {
          width: 100%;
          padding: 1rem;
          background: #0a0a0a;
          border: 1px solid #333;
          border-radius: 8px;
          color: #ffffff;
          font-size: 0.95rem;
          font-family: inherit;
          resize: vertical;
          min-height: 80px;
          transition: all 0.2s ease;
        }

        .comment-input:focus {
          outline: none;
          border-color: #555;
          background: #0a0a0a;
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
        }

        .comment-input::placeholder {
          color: #666;
        }

        .input-controls {
          display: flex;
          justify-content: flex-end;
          width: 100%
        }

        .submit-button {
          padding: 0.75rem 2rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 300;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          width: 100%
        }

        .submit-button:disabled {
          background: #333;
          color: #666;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .comments-section {
          display: flex;
          flex-direction: column;
        }

        .comments-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .pagination-button {
          padding: 0.5rem 1rem;
          background: #1a1a1a;
          color: #fff;
          border: 1px solid #333;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .pagination-button:hover:not(:disabled) {
          background: #2a2a2a;
          border-color: #555;
        }

        .pagination-button:disabled {
          background: #1a1a1a;
          color: #444;
          border-color: #333;
          cursor: not-allowed;
        }

        .page-indicator {
          color: #aaa;
          font-size: 0.85rem;
          font-weight: 500;
          min-width: 70px;
          text-align: center;
        }

        .comments-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          max-height: 500px;
          overflow-y: auto;
          padding-right: 0.5rem;
        }

        .comments-list::-webkit-scrollbar {
          width: 6px;
        }

        .comments-list::-webkit-scrollbar-track {
          background: #1a1a1a;
          border-radius: 3px;
        }

        .comments-list::-webkit-scrollbar-thumb {
          background: #333;
          border-radius: 3px;
        }

        .comments-list::-webkit-scrollbar-thumb:hover {
          background: #444;
        }

        .comment-card {
          background: #0a0a0a;
          border-radius: 8px;
          border: 1px solid #333;
          transition: all 0.2s ease;
        }

        .comment-content {
          padding: 1.25rem;
        }

        .comment-text {
          font-size: 0.95rem;
          color: #e0e0e0;
          line-height: 1.6;
          margin-bottom: 1rem;
          word-break: break-word;
        }

        .comment-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .star-rating {
          display: flex;
          gap: 0.2rem;
        }

        .comment-meta {
          font-size: 0.8rem;
          color: #888;
          font-weight: 500;
        }

        .no-comments {
          font-size: 0.9rem;
          color: #666;
          text-align: center;
          padding: 3rem 2rem;
          background: #0a0a0a;
          border-radius: 8px;
          border: 1px dashed #333;
        }

        .loading-spinner {
          border: 2px solid #333;
          border-top: 2px solid #667eea;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 1s linear infinite;
          margin: 2rem auto;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .interaction-container {
            padding: 1.5rem;
            gap: 1.5rem;
          }

          .comments-header {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .pagination-controls {
            justify-content: center;
          }

          .comment-footer {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .star-input-group {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default React.memo(DatasetInteraction);